<script setup>
import { onMounted, onUnmounted, reactive, ref } from 'vue'
import { replaceS3path } from '@/Shared/helper.js'
import IconProduct from '@/Shared/Icons/IconProduct'
import DetailPanel from '@/Pages/Brand/V2Components/DetailV2ProductDetailPanel'
import useIntersectionObserver from '@/Shared/useIntersectionObserver.js'

const props = defineProps({
  brandInfo: Array,
  myCategories: Array,
  myProducts: Array,
})

const targetRef = ref()
const isNavFixed = ref(false)

// filter categories for visible true
const onlyVisibles = () => {
  return props.myCategories.filter(i => i.visible === 'true')
}

// for products update hook
const currentProducts = reactive(
  {
    items: props.myProducts,
    menustats: false,
    focus: -1,
    category: 'すべて',
  },
)

// click action on category 
const clickOnCategory = (slug, category) => {
  // category menu on
  currentProducts.menustats = false
  // filter products by category
  if(slug==='all'){
    currentProducts.items = props.myProducts
  }else{
    currentProducts.items = props.myProducts.filter(i => i.productCategoryMain === slug)
  }
  // close detail panel
  currentProducts.focus = -1
  // set category name
  currentProducts.category = category

  scrollToElement('.item-list')
}

// join s3 path from DB and productImage
const generateImgPath = function (img) {
  const r = props.brandInfo[0].brandPMSs3path + '500/' + img
  return r
}
// category menu on/off
const clickOnCategoryMenu = function(){
  currentProducts.menustats = !currentProducts.menustats
}
// focus product with detail panel open
const focusItem = function(n){
  currentProducts.focus = n
}
// check match focusItem
const matchIndex = function(a,b){
  const r = ( a === b ) ? true : false
  return r
}
// close detail panel
const closePanel = function(){
  currentProducts.focus = -1
  scrollToElement('.item-list .each.active', 'smooth')
}

const scrollToElement = function (target, behavior = 'instant') {
  var el = document.querySelector(target)
  el.scrollIntoView({ 
    block: 'start',
    behavior: behavior,
  })
}

const timer = ref(null)
const scrollToElementWithDelay = function(element, behavior = 'instant'){
  if (typeof window === 'undefined') return
  timer.value = setTimeout(function () {
    scrollToElement(element, behavior)
  }, 100)
}

const buildThresholdList = (steps) => {
  let thresholds = [0]
  for (let i = 1.0; i <= steps; i++) {
    thresholds.push(i / steps)
  }
  return thresholds
}

const observeNav = () => {
  const offset = document.querySelector('.header').offsetHeight + 20
  useIntersectionObserver({
    target: targetRef.value,
    threshold: buildThresholdList(700),
    rootMargin: '0px',
    onEnter: (entry) => {
      if(entry.boundingClientRect.top<=offset){
        isNavFixed.value = true
      }else{
        isNavFixed.value = false
      }
    },
    onLeave: () => {
      isNavFixed.value = false
    },
  })
}

onMounted(() => {
  observeNav()
})

onUnmounted(() => {
  clearTimeout(timer)
})

</script>

<template>
  <div class="brand-products">
    <h2 class="lg:block font-rift font-bold text-center text-[50px] xs:text-[60px] sm:text-[70px] md:text-[80px] lg:text-[80px] mb-10 md:mb-20">
      PRODUCTS
      <small class="font-jpsans block mt-2 sm:mt-3 text-[15px] sm:text-[18px]">商品一覧</small>
    </h2>
    <div ref="targetRef" class="min-h-screen">
      <div class="product-header absolute" :class="{'bg-white': currentProducts.menustats, 'nav-fixed': isNavFixed}">
        <div class="inner absolute w-full">
          <template v-if="currentProducts.menustats">
            <div class="product-title text-left mt-1 text-[15px] sm:text-[16px] font-bold absolute">カテゴリーを選んでください</div>
          </template>
          <div class="flex justify-end items-center cursor-pointer w-auto relative z-40 mt-2" @click="clickOnCategoryMenu()">
            <template v-if="!currentProducts.menustats">
              <span class="text-[14px] sm:text-[15px] font-bold block w-auto pr-2">カテゴリー</span>
              <span><IconProduct type="category" classname="icon-category !h-[22px] !w-[22px]" /></span>
            </template>
            <template v-else>
              <span><IconProduct type="close" classname="icon-close !h-[22px] !w-[22px]" /></span>
            </template>
          </div>
        </div>
        <transition name="fade-category">
          <div v-show="currentProducts.menustats" class="product-category !mb-0">
            <ul class="category-list v2">
              <li class="each-category" @click="clickOnCategory('all', 'すべて')">
                <div class="img p-[2px]"><img class="aspect-square" :src="replaceS3path(props.brandInfo[0].brandThumb)" alt="すべて" /></div>
                <div class="category-name">すべて</div>
              </li>
              <li v-for="c in onlyVisibles()" :key="c" class="each-category" @click="clickOnCategory(c.slug, c.name)">
                <div class="img"><img :src="c.img" :alt="c.name" /></div>
                <div class="category-name">{{ c.name }}</div>
              </li>
            </ul>
          </div>
        </transition>
      </div>
    
      <transition name="fade-category">
        <div :key="currentProducts.category" class="item-list scroll-mt-[80px] md:scroll-mt-[100px] xl:scroll-mt-[50px]">
          <div class="mx-auto mb-8 font-bold xl:max-w-[840px] 2xl:max-w-[1000px] 3xl:max-w-[1200px] 4xl:max-w-[1400px]">
            <div class="product-title text-left text-[11px] sm:text-[12px] md:text-[13px] mb-[6px]">カテゴリー</div>
            <div class="text-[16px] sm:text-[18px] md:text-[20px]">{{ currentProducts.category }}</div>
          </div>

          <div v-for="p, index in currentProducts.items" :key="p" class="each scroll-mt-[80px] md:scroll-mt-[100px] xl:scroll-mt-[50px]" :class="[{ active: matchIndex(index,currentProducts.focus)}]">
            <div class="each-inner" @click="[focusItem(index), scrollToElementWithDelay('.detailcontent','smooth')]">
              <div class="img"><img :src="generateImgPath(p.productImage)" :alt="p.productNameJ" class="" /></div>
              <div class="product-name">
                <span v-if="p.productNewFlag" class="new-box">NEW</span>
                {{ p.productNameJ }}
              </div>
            </div>
  
            <template v-if="matchIndex(currentProducts.focus, index)">
              <div ref="panelRef">
                <DetailPanel
                  :keyindex="index"
                  :brand-info="brandInfo"
                  :close-action="closePanel"
                  :focus="currentProducts.focus"
                  :product="p"
                  types="v2"
                />
              </div>
            </template>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>
